import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { logger } from './logger';

const DEFAULT_TIMEOUT = 30000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

interface RetryConfig extends AxiosRequestConfig {
  _retryCount?: number;
}

const apiClient = axios.create({
  baseURL: '/api',
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    logger.debug('API', `${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    logger.error('API', 'Request error', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    logger.debug('API', `Response ${response.status} from ${response.config.url}`);
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as RetryConfig | undefined;

    if (!config) {
      return Promise.reject(error);
    }

    const shouldRetry =
      error.response?.status === 429 ||
      error.response?.status === 503 ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ETIMEDOUT';

    if (!shouldRetry) {
      logger.error('API', `Request failed: ${error.message}`, error.response?.data);
      return Promise.reject(error);
    }

    config._retryCount = config._retryCount ?? 0;

    if (config._retryCount >= MAX_RETRIES) {
      logger.error('API', `Max retries (${MAX_RETRIES}) exceeded for ${config.url}`);
      return Promise.reject(error);
    }

    config._retryCount++;
    const delay = RETRY_DELAY * config._retryCount;

    logger.warn('API', `Retrying request (${config._retryCount}/${MAX_RETRIES}) after ${delay}ms`);

    await new Promise((resolve) => setTimeout(resolve, delay));

    return apiClient(config);
  }
);

export default apiClient;
